import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import AdminApp from './AdminApp';
import '../globals.css';
import '../i18n';
import { setTimezone } from '@/shared/lib/format';
import { readBlobError, saveRenderedReport, setExportContext } from '@/shared/lib/export-context';
import i18n, { getLanguage } from '@/i18n';
import { plat, restoreAdminSession, useAdmin } from './api';

// Netxol staff look after ISPs in many countries: dates and times in the staff member's own time zone
setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
// exports from Netxol Admin are Netxol's: recorded in the platform audit trail, never through the console's API
setExportContext({
  company: () => 'Netxol',
  who: () => { const u = useAdmin.getState().user; return u?.name ?? u?.email ?? ''; },
  log: (e) => plat.post('/exports/log', e),
  // Netxol Report rendered by the platform in Netxol's brand
  render: async (doc, format) => saveRenderedReport(await plat.post('/reports/render', doc, { params: { format, lang: getLanguage() }, responseType: 'blob', timeout: 300_000 }).catch(readBlobError), `report.${format}`),
  section: () => String(i18n.t('export.netxol_admin')),
});
if (localStorage.getItem('nx.theme') === 'dark') document.documentElement.classList.add('dark');
void restoreAdminSession();   // access token from the HttpOnly refresh cookie (none is kept in web storage)
const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 20_000, refetchOnWindowFocus: false, retry: 1 } } });
// admin.one.netxol.com serves the panel at its root; in development it lives under /netxol-admin on the console's address
const basename = location.pathname.startsWith('/netxol-admin') ? '/netxol-admin' : '';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter basename={basename}>
      <QueryClientProvider client={queryClient}>
        <AdminApp />
        <Toaster richColors position="top-right" closeButton />
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
